import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CircularProgress, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronLeft,
  faChevronUp,
  faMagnifyingGlass,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import {
  useLabraChatContext,
  useLabraChatDispatch,
} from '../../../modules/labraChat/context'
import {
  closeLabraChatHistory,
  loadLabraChatSession,
} from '../../../modules/labraChat/actions'
import { labraSupportUrl } from '../../../utils/constants'
import { useChatConversations } from '../../../modules/labraChat/useChatConversations'
import { ChatHistoryRow } from './ChatHistoryRow'
import {
  bucketConversationsByRecency,
  hasReachedLockedRange,
  LOCKED_GROUPS,
  TODAY,
} from './recency'
import { useStyles } from './ChatHistoryView.styles'

interface ChatHistoryViewProps {
  /** Adds wider side gutters when shown in the full-screen (maximized) panel. */
  isFullScreen?: boolean
}

export const ChatHistoryView: React.FC<ChatHistoryViewProps> = ({
  isFullScreen = false,
}) => {
  const { classes, cx } = useStyles()
  const dispatch = useLabraChatDispatch()
  const { partnerId } = useLabraChatContext()

  const [query, setQuery] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<string[]>([TODAY])
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null)
  const [selectionError, setSelectionError] = useState(false)

  const { conversations, isLoading, isError, hasMore, loadMore } =
    useChatConversations(partnerId, query)

  const groups = useMemo(
    () => bucketConversationsByRecency(conversations),
    [conversations]
  )

  useEffect(() => {
    if (hasMore && !isLoading && !hasReachedLockedRange(conversations)) {
      loadMore()
    }
  }, [hasMore, isLoading, conversations, loadMore])

  const isExpanded = (label: string) => expandedGroups.includes(label)

  const toggleGroup = (label: string) =>
    setExpandedGroups(expanded =>
      expanded.includes(label)
        ? expanded.filter(open => open !== label)
        : [...expanded, label]
    )

  const handleSelect = useCallback(
    async (sessionId: string) => {
      if (loadingSessionId) return
      setLoadingSessionId(sessionId)
      setSelectionError(false)
      const loaded = await dispatch(loadLabraChatSession(partnerId, sessionId))
      if (loaded) {
        dispatch(closeLabraChatHistory())
      } else {
        setLoadingSessionId(null)
        setSelectionError(true)
      }
    },
    [dispatch, partnerId, loadingSessionId]
  )

  const hasQuery = query.trim().length > 0
  const noConversations = !isLoading && !isError && conversations.length === 0

  const groupHeader = (label: string, count?: number) => (
    <button
      className={classes.groupHeader}
      type="button"
      onClick={() => toggleGroup(label)}
      aria-expanded={isExpanded(label)}
    >
      <span className={classes.groupLabel}>{label}</span>
      <span className={classes.groupHairline} />
      {count !== undefined && (
        <span className={classes.groupCount}>{count}</span>
      )}
      <span className={classes.groupDivider} />
      <FontAwesomeIcon icon={isExpanded(label) ? faChevronUp : faChevronDown} />
    </button>
  )

  return (
    <div
      className={cx(classes.root, {
        [classes.rootFullScreen]: isFullScreen,
      })}
    >
      <div className={classes.titleRow}>
        <button
          className={classes.backButton}
          type="button"
          onClick={() => dispatch(closeLabraChatHistory())}
          aria-label="Back to chat"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span className={classes.heading}>Chat history</span>
      </div>

      <div className={classes.searchWrap}>
        <div
          className={cx(classes.searchField, {
            [classes.searchFieldFocused]: isSearchFocused,
          })}
        >
          <input
            className={classes.searchInput}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            placeholder="Search chats"
          />
          {hasQuery ? (
            <button
              className={classes.clearButton}
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          ) : (
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          )}
        </div>
      </div>

      <div className={classes.list}>
        {selectionError && (
          <div className={classes.stateMessage} role="alert">
            <div className={classes.stateTitle}>
              Unable to load this conversation.
            </div>
            <div className={classes.stateHint}>Please try again.</div>
          </div>
        )}
        {isError && conversations.length === 0 && (
          <div className={classes.stateMessage}>
            <div className={classes.stateTitle}>
              Couldn&apos;t load your chats
            </div>
            <div className={classes.stateHint}>Please try again.</div>
          </div>
        )}

        {hasQuery && noConversations && (
          <div className={classes.stateMessage}>
            <div className={classes.stateTitle}>No chats found</div>
            <div className={classes.stateHint}>
              Try a different search term.
            </div>
          </div>
        )}

        {isLoading && conversations.length === 0 && (
          <div className={classes.centerRow}>
            <CircularProgress size={20} thickness={5} />
          </div>
        )}

        {groups.map(group => (
          <div key={group.label} className={classes.group}>
            {groupHeader(group.label, group.items.length)}
            {isExpanded(group.label) &&
              group.items.map(conversation => (
                <ChatHistoryRow
                  key={conversation.sessionId}
                  title={conversation.title}
                  updatedAt={conversation.updatedAt}
                  isLoading={loadingSessionId === conversation.sessionId}
                  disabled={loadingSessionId !== null}
                  onSelect={() => handleSelect(conversation.sessionId)}
                />
              ))}
          </div>
        ))}

        {!hasQuery &&
          LOCKED_GROUPS.map(({ label, period }) => (
            <div key={label} className={classes.group}>
              {groupHeader(label)}
              {isExpanded(label) && (
                <div className={classes.lockedBody}>
                  <Typography className={classes.lockedText}>
                    Unlock your full chat history beyond {period}. Upgrade your
                    plan to access older conversations and never lose important
                    context.
                  </Typography>
                  <a
                    className={classes.upgradeButton}
                    href={labraSupportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Request to enable
                  </a>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  )
}
